import { useCallback, useEffect, useState } from 'react';
import { api, openAuthedDocument } from '../lib/api';
import { ErrorAlert, Loading } from '../components/ui';
import { useAuth } from '../auth';

/**
 * เพดานการใช้งาน และการเลิกใช้บริการ
 *
 * ─── หน้านี้ต้องไม่ทำให้เจ้าหน้าที่กลัว ────────────────────────────────────
 *
 * แถบสีแดงบนหน้าจอมีพลังมากกว่าที่ควร เจ้าหน้าที่ที่เห็นแล้วไม่กล้าบันทึกเคสใหม่
 * คือความเสียหายที่เกิดจากการออกแบบหน้าจอ ไม่ได้เกิดจากระบบ — หน้านี้จึงเขียนไว้
 * ทุกจุดว่าอะไร "จะไม่ถูกหยุด" ไม่ใช่แค่แสดงตัวเลขที่เกินแล้วปล่อยให้เดาเอง
 *
 * ─── ส่วนปิดมูลนิธิตั้งใจให้กดยาก ─────────────────────────────────────────
 *
 * ซ่อนอยู่หลังปุ่ม "เปิดส่วนเลิกใช้บริการ" และต้องพิมพ์เหตุผลเอง เพราะการกดผิด
 * ที่นี่ตามมาด้วยการลบข้อมูลถาวร ส่วนการดาวน์โหลดข้อมูลอยู่ข้างนอกและกดง่าย —
 * เอาข้อมูลของตัวเองออกไปเป็นสิทธิ์ ไม่ใช่ขั้นตอนของการเลิกใช้
 */

interface QuotaLine {
  key: string;
  label: string;
  used: number;
  limit: number;
  ratio: number | null;
  state: 'ok' | 'warn' | 'over';
  blocks: boolean;
}

interface Quota {
  lines: QuotaLine[];
  anyOver: boolean;
  anyWarn: boolean;
  note: string;
}

interface Closure {
  status: string;
  requestedAt: string | null;
  reason: string | null;
  exportReadyAt: string | null;
  exportDownloadedAt: string | null;
  purgedAt: string | null;
  canPurgeAfter: string | null;
  remainingSteps: string[];
  graceDays: number;
}

const num = (n: number) => n.toLocaleString('th-TH');

export default function OrgLifecycle() {
  const { me } = useAuth();
  const isSuperAdmin = me?.role === 'super_admin';

  const [quota, setQuota] = useState<Quota | null>(null);
  const [closure, setClosure] = useState<Closure | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [flash, setFlash] = useState<string | null>(null);
  const [showDanger, setShowDanger] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      setQuota(await api<Quota>('/org/quota'));
      if (isSuperAdmin) setClosure(await api<Closure>('/org/closure'));
    } catch (e) {
      setError(e);
    }
  }, [isSuperAdmin]);

  useEffect(() => {
    void load();
  }, [load]);

  async function download() {
    setError(null);
    setFlash(null);
    setBusy(true);
    try {
      await openAuthedDocument('/org/export', {
        download: `ข้อมูลมูลนิธิ-${new Date().toISOString().slice(0, 10)}.json`,
      });
      setFlash('ดาวน์โหลดแล้ว — เก็บไฟล์นี้ไว้ในที่ปลอดภัย เพราะมีข้อมูลส่วนบุคคลอยู่ข้างใน');
      await load();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  async function requestClosure(form: HTMLFormElement) {
    const reason = String(new FormData(form).get('reason') ?? '').trim();
    if (!window.confirm(`ยืนยันยื่นคำขอปิดมูลนิธิ?\n\nระบบจะหยุดรับข้อมูลใหม่ทันที แต่ยังเปิดดูและดาวน์โหลดของเดิมได้ และยกเลิกได้ตลอด ${closure?.graceDays ?? 30} วัน`)) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await api<{ nextSteps: string[] }>('/org/closure', {
        method: 'POST',
        body: { reason },
      });
      setFlash(res.nextSteps.join(' · '));
      await load();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  async function cancel() {
    setBusy(true);
    setError(null);
    try {
      await api('/org/closure', { method: 'DELETE' });
      setFlash('ยกเลิกคำขอปิดแล้ว — กลับมาใช้งานได้ตามปกติทันที ข้อมูลอยู่ครบเหมือนเดิม');
      setShowDanger(false);
      await load();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h1>การใช้งานและข้อมูลของมูลนิธิ</h1>
          <p>ดูปริมาณการใช้งาน ดาวน์โหลดข้อมูลทั้งหมด และจัดการการเลิกใช้บริการ</p>
        </div>
      </div>

      <ErrorAlert error={error} />
      {flash && <div className="alert success">{flash}</div>}

      {closure?.requestedAt && !closure.purgedAt && (
        <div className="alert warn">
          <strong>มีคำขอปิดมูลนิธิค้างอยู่</strong> (ยื่นเมื่อ{' '}
          {new Date(closure.requestedAt).toLocaleDateString('th-TH')}) — ระบบหยุดรับข้อมูลใหม่แล้ว
          แต่ยังเปิดดูและดาวน์โหลดของเดิมได้ทั้งหมด
          {closure.canPurgeAfter && (
            <>
              <br />
              ข้อมูลจะถูกลบถาวรได้หลัง{' '}
              {new Date(closure.canPurgeAfter).toLocaleDateString('th-TH')} —{' '}
              <strong>เปลี่ยนใจได้จนถึงก่อนหน้านั้น</strong>
            </>
          )}
        </div>
      )}

      {/* ------------------------------------------------------------- เพดาน */}

      {!quota ? (
        <Loading />
      ) : (
        <div className="card">
          <h2>ปริมาณการใช้งาน</h2>
          <p className="hint">{quota.note}</p>

          <table style={{ marginTop: 12 }}>
            <thead>
              <tr>
                <th>รายการ</th>
                <th style={{ textAlign: 'right' }}>ใช้ไป</th>
                <th style={{ textAlign: 'right' }}>เพดาน</th>
                <th>สถานะ</th>
              </tr>
            </thead>
            <tbody>
              {quota.lines.map((l) => (
                <tr key={l.key}>
                  <td>{l.label}</td>
                  <td style={{ textAlign: 'right' }}>{num(l.used)}</td>
                  <td style={{ textAlign: 'right' }}>{l.limit === 0 ? 'ไม่จำกัด' : num(l.limit)}</td>
                  <td>
                    {l.state === 'ok' ? (
                      <span className="badge green">ปกติ</span>
                    ) : l.state === 'warn' ? (
                      <span className="badge amber">ใกล้เต็ม</span>
                    ) : (
                      <span className={`badge ${l.blocks ? 'red' : 'amber'}`}>
                        {l.blocks ? 'เต็มแล้ว' : 'เกินแล้ว (ยังใช้งานต่อได้)'}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {quota.anyOver && (
            <div className="alert info" style={{ marginTop: 12 }}>
              เกินเพดานบางรายการแล้ว — <strong>งานประจำวันทำต่อได้ตามปกติ</strong>{' '}
              การบันทึกเงินบริจาค เคส และผู้รับความช่วยเหลือจะไม่ถูกหยุด
              ผู้ดูแลระบบจะติดต่อไปคุยเรื่องขยายเพดานให้
            </div>
          )}
        </div>
      )}

      {/* -------------------------------------------------------- ดาวน์โหลด */}

      {isSuperAdmin && (
        <div className="card">
          <h2>ดาวน์โหลดข้อมูลทั้งหมด</h2>
          <p className="hint">
            ได้ไฟล์เดียวที่มีข้อมูลทุกตารางของมูลนิธิ ใช้เก็บสำรองหรือย้ายไประบบอื่นก็ได้
            ไม่ต้องขอปิดมูลนิธิก่อน — ข้อมูลเป็นของคุณ เอาออกไปเมื่อไรก็ได้
            <br />
            รูปภาพและเอกสารอยู่บน Google Drive ของมูลนิธิอยู่แล้ว จึงไม่ได้รวมมาในไฟล์นี้
          </p>
          <div className="btn-row">
            <button className="btn" disabled={busy} onClick={() => void download()}>
              ดาวน์โหลดข้อมูลทั้งหมด
            </button>
          </div>
          {closure?.exportDownloadedAt && (
            <p className="hint">
              ดาวน์โหลดครั้งล่าสุดระหว่างขั้นตอนปิด:{' '}
              {new Date(closure.exportDownloadedAt).toLocaleString('th-TH')}
            </p>
          )}
        </div>
      )}

      {/* ------------------------------------------------------- เลิกใช้บริการ */}

      {isSuperAdmin && closure && !closure.purgedAt && (
        <div className="card">
          <h2>เลิกใช้บริการ</h2>

          {!showDanger && !closure.requestedAt ? (
            <>
              <p className="hint">
                ถ้ามูลนิธิจะยุติการดำเนินงานหรือย้ายไปใช้ระบบอื่น ยื่นคำขอปิดได้ที่นี่
              </p>
              <div className="btn-row">
                <button className="btn ghost" onClick={() => setShowDanger(true)}>
                  เปิดส่วนเลิกใช้บริการ
                </button>
              </div>
            </>
          ) : closure.requestedAt ? (
            <>
              <p className="hint">
                เหตุผลที่ยื่นไว้: {closure.reason}
              </p>
              <ol className="hint" style={{ paddingLeft: 20 }}>
                <li>
                  {closure.exportDownloadedAt ? '✓' : '✗'} ดาวน์โหลดข้อมูลทั้งหมดไปเก็บไว้แล้ว
                  {!closure.exportDownloadedAt && ' — กดปุ่มด้านบน (ระบบจะไม่ลบข้อมูลจนกว่าจะโหลดไปแล้ว)'}
                </li>
                <li>
                  {closure.canPurgeAfter && new Date().toISOString() >= closure.canPurgeAfter
                    ? '✓'
                    : '✗'}{' '}
                  ครบระยะเวลารอ {closure.graceDays} วัน
                </li>
              </ol>

              <div className="alert warn">
                <strong>เปลี่ยนใจได้ตลอดจนถึงก่อนลบจริง</strong> — กดยกเลิกแล้วกลับมาใช้งานได้ทันที
                ข้อมูลทั้งหมดยังอยู่ครบไม่ได้หายไปไหน
              </div>

              <div className="btn-row">
                <button className="btn" disabled={busy} onClick={() => void cancel()}>
                  ยกเลิกคำขอปิด กลับมาใช้งานต่อ
                </button>
              </div>
            </>
          ) : (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                void requestClosure(e.currentTarget);
              }}
            >
              <div className="alert warn">
                หลังยื่นคำขอ ระบบจะ<strong>หยุดรับข้อมูลใหม่ทันที</strong> แต่ยังเปิดดูและ
                ดาวน์โหลดของเดิมได้ครบ และ<strong>ยกเลิกได้ตลอด {closure.graceDays} วัน</strong>
                <br />
                เมื่อพ้น {closure.graceDays} วันและดาวน์โหลดข้อมูลไปแล้ว ผู้ดูแลระบบจึงจะลบข้อมูล
                ถาวรได้ — ขั้นนั้น<strong>ย้อนกลับไม่ได้</strong>
              </div>

              <label>
                เหตุผล <span className="hint">อย่างน้อย 10 ตัวอักษร</span>
                <textarea name="reason" required minLength={10} maxLength={500} rows={3} />
              </label>

              <div className="btn-row">
                <button className="btn danger" disabled={busy}>
                  ยื่นคำขอปิดมูลนิธิ
                </button>
                <button type="button" className="btn ghost" onClick={() => setShowDanger(false)}>
                  ยกเลิก
                </button>
              </div>
            </form>
          )}
        </div>
      )}

      {closure?.purgedAt && (
        <div className="alert error">
          ข้อมูลของมูลนิธินี้ถูกลบถาวรแล้วเมื่อ{' '}
          {new Date(closure.purgedAt).toLocaleString('th-TH')}
        </div>
      )}
    </>
  );
}
